import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import RoleChip from 'components/chip/RoleChip';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockListHeader from '../components/GuideMockListHeader';
import GuideMockTextField from '../components/GuideMockTextField';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideUserRegister: React.FC = () => {
  return (
    <GuidePageLayout
      title="新規ユーザーを登録して権限を付与する"
      description="新しいユーザーアカウントを作成し、適切な権限を設定します。"
      breadcrumbs={[{ label: 'ユーザー管理', path: '/guide/user/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者'],
            ['ログイン状態', 'ログイン済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            {
              label: '管理者メニューからユーザー管理画面を開く',
            },
            {
              label: '一覧パネル右上の「+」アイコンを押下して登録フォームを開く',
              content: (
                <GuideMockContainer label="リストヘッダー">
                  <GuideMockListHeader createLabel="ユーザー新規作成" />
                </GuideMockContainer>
              ),
            },
            {
              label: 'ユーザー情報を入力する',
              description:
                'フォームタイトルは「ユーザー登録」です。タイトル左の戻る矢印をクリックすると、登録を破棄して一覧に戻ります。',
              content: (
                <GuideMockContainer label="登録フォーム">
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        ユーザー登録
                      </Typography>
                    </Stack>
                    <GuideMockTextField label="ユーザーID" required />
                    <GuideMockTextField label="ユーザー名" required />
                    <GuideMockTextField label="メールアドレス" required />
                    <GuideMockTextField label="パスワード" required type="password" />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「作成」ボタンを押下してユーザーを作成する',
              content: (
                <GuideMockContainer label="フッターボタン">
                  <GuideMockButton label="作成" startIcon={<PersonAddIcon />} />
                </GuideMockContainer>
              ),
            },
            {
              label: '作成されたユーザーの権限を設定する',
              description:
                '作成後、ユーザー詳細画面が表示されます。「編集」ボタンを押して編集モードに入り、権限セクションでロールChipをクリックして権限を切り替えます。',
              content: (
                <GuideMockContainer label="権限設定（クリックで切替）">
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'action.hover',
                      border: 1,
                      borderColor: 'divider',
                      pointerEvents: 'none',
                    }}
                  >
                    <RoleChip role="ADMIN" variant="filled" />
                    <RoleChip role="BACK_OFFICE" variant="outlined" />
                    <RoleChip role="MANAGER" variant="outlined" />
                  </Stack>
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="入力項目">
        <GuideTable
          headers={['項目名', '必須', '形式', '備考']}
          rows={[
            ['ユーザーID', '必須', 'テキスト（最大300桁）', 'ログインに使用するID。重複不可'],
            ['メールアドレス', '必須', 'テキスト（最大300桁）', ''],
            ['ユーザー名', '必須', 'テキスト（最大300桁）', '表示名'],
            ['パスワード', '必須', 'テキスト（マスク表示、最大300桁）', '初期パスワード'],
            ['権限', '必須（1つ以上）', 'ロールChipクリック切替', '管理者 / 管理部門 / 課長'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '動作']}
          rows={[
            ['← 戻る矢印（タイトル左）', '登録を破棄して一覧画面に戻る'],
            ['作成ボタン', '入力内容でユーザーを作成する'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox type="warning">
            ユーザーIDは一度登録すると変更できません。慎重に設定してください。
          </GuideInfoBox>
          <GuideInfoBox type="warning">
            同じユーザーIDが既に存在する場合はエラーになります。
          </GuideInfoBox>
          <GuideInfoBox>
            登録直後は権限なしの状態です。必ず権限設定を行ってください。権限は最低1つ選択する必要があります。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideUserRegister;
